import React from 'react';
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  GitCompare,
  Search,
  FileText,
  BarChart3,
} from 'lucide-react';

export type ComparisonStatus = 'idle' | 'preparing' | 'comparing' | 'analyzing' | 'completed' | 'error';

export interface ComparisonProgressStep {
  id: string;
  label: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  icon: React.ElementType;
}

export interface ComparisonProgressProps {
  status: ComparisonStatus;
  progress?: number; // 0-100
  currentStep?: string;
  errorMessage?: string;
  onRetry?: () => void;
  onCancel?: () => void;
  testId?: string;
}

const getSteps = (status: ComparisonStatus): ComparisonProgressStep[] => {
  const steps: ComparisonProgressStep[] = [
    {
      id: 'prepare',
      label: 'Preparing Contracts',
      description: 'Loading contract data and clauses',
      status: 'pending',
      icon: FileText,
    },
    {
      id: 'compare',
      label: 'Comparing Clauses',
      description: 'Finding similar and differing clauses',
      status: 'pending',
      icon: GitCompare,
    },
    {
      id: 'analyze',
      label: 'Analyzing Gaps',
      description: 'Identifying gaps and recommendations',
      status: 'pending',
      icon: Search,
    },
    {
      id: 'complete',
      label: 'Generating Results',
      description: 'Preparing comparison report',
      status: 'pending',
      icon: BarChart3,
    },
  ];

  // Update step statuses based on comparison status
  switch (status) {
    case 'idle':
      break;
    case 'preparing':
      steps[0].status = 'in_progress';
      break;
    case 'comparing':
      steps[0].status = 'completed';
      steps[1].status = 'in_progress';
      break;
    case 'analyzing':
      steps[0].status = 'completed';
      steps[1].status = 'completed';
      steps[2].status = 'in_progress';
      break;
    case 'completed':
      steps.forEach((step) => (step.status = 'completed'));
      break;
    case 'error':
      // Find the in_progress step and mark it as error
      const inProgressIndex = steps.findIndex((s) => s.status === 'pending');
      if (inProgressIndex > 0) {
        for (let i = 0; i < inProgressIndex; i++) {
          steps[i].status = 'completed';
        }
        steps[inProgressIndex - 1].status = 'error';
      } else {
        steps[0].status = 'error';
      }
      break;
  }

  return steps;
};

export const ComparisonProgress: React.FC<ComparisonProgressProps> = ({
  status,
  progress = 0,
  currentStep,
  errorMessage,
  onRetry,
  onCancel,
  testId = 'comparison-progress',
}) => {
  const steps = getSteps(status);

  const getStepIcon = (step: ComparisonProgressStep) => {
    const Icon = step.icon;
    switch (step.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <Loader2 className="h-5 w-5 text-primary-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Icon className="h-5 w-5 text-gray-300" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-primary-500';
    }
  };

  if (status === 'idle') {
    return null;
  }

  return (
    <div
      className="bg-white rounded-lg shadow-md p-6 space-y-4"
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {status === 'completed' ? (
            <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
          ) : status === 'error' ? (
            <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
          ) : (
            <Loader2 className="h-6 w-6 text-primary-500 animate-spin mr-3" />
          )}
          <div>
            <h3
              className={`font-semibold ${
                status === 'error' ? 'text-red-900' : 'text-gray-900'
              }`}
              data-testid={`${testId}-title`}
            >
              {status === 'completed'
                ? 'Comparison Complete'
                : status === 'error'
                ? 'Comparison Failed'
                : 'Comparison in Progress'}
            </h3>
            {currentStep && status !== 'completed' && status !== 'error' && (
              <p className="text-sm text-gray-500" data-testid={`${testId}-current-step`}>
                {currentStep}
              </p>
            )}
          </div>
        </div>
        {status !== 'completed' && status !== 'error' && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700"
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </button>
        )}
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2" data-testid={`${testId}-progress-bar`}>
        <div
          className={`h-2 rounded-full transition-all duration-300 ${getStatusColor()}`}
          style={{ width: `${status === 'completed' ? 100 : progress}%` }}
          data-testid={`${testId}-progress-fill`}
        />
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`flex items-start space-x-3 ${
              step.status === 'pending' ? 'opacity-50' : ''
            }`}
            data-testid={`${testId}-step-${step.id}`}
          >
            <div className="flex-shrink-0 mt-0.5">{getStepIcon(step)}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span
                  className={`text-sm font-medium ${
                    step.status === 'error'
                      ? 'text-red-900'
                      : step.status === 'pending'
                      ? 'text-gray-400'
                      : 'text-gray-900'
                  }`}
                >
                  {step.label}
                </span>
                {step.status === 'in_progress' && (
                  <Clock className="h-4 w-4 text-gray-400" />
                )}
              </div>
              <p
                className={`text-xs ${
                  step.status === 'error'
                    ? 'text-red-600'
                    : 'text-gray-500'
                }`}
              >
                {step.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Error message and retry */}
      {status === 'error' && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800" data-testid={`${testId}-error-message`}>
            {errorMessage || 'An error occurred during comparison. Please try again.'}
          </p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
              data-testid={`${testId}-retry`}
            >
              Retry Comparison
            </button>
          )}
        </div>
      )}

      {/* Completion message */}
      {status === 'completed' && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800" data-testid={`${testId}-success-message`}>
            Comparison completed successfully. View the results below.
          </p>
        </div>
      )}
    </div>
  );
};

export default ComparisonProgress;
